/**
 * NotificationPanel — Header Bell inbox.
 *
 * Scan-first rows: icon + title + two-line body + relative time.
 * Unread is a single left accent + heavier title (not a pile of red signals).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell, Eye, MessageCircle, Users, Upload, FileWarning, CheckCheck, Loader2, Database,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
    hrefForNotification,
    useNotificationStore,
} from '../../stores/useNotificationStore';
import type { InboxNotification, NotificationCategory } from '../../services/api';

function relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const m = Math.max(0, Math.round((Date.now() - then) / 60_000));
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
}

function categoryIcon(category: NotificationCategory | string) {
    const cls = 'w-3.5 h-3.5 shrink-0';
    switch (category) {
        case 'watchlist_alert':
            return <Eye className={clsx(cls, 'text-amber-500')} aria-hidden="true" />;
        case 'feedback_reply':
            return <MessageCircle className={clsx(cls, 'text-primary')} aria-hidden="true" />;
        case 'case_shared':
            return <Users className={clsx(cls, 'text-sky-500')} aria-hidden="true" />;
        case 'import_complete':
            return <Upload className={clsx(cls, 'text-muted-foreground')} aria-hidden="true" />;
        case 'auto_case':
            return <FileWarning className={clsx(cls, 'text-orange-500')} aria-hidden="true" />;
        case 'scraper_run':
            return <Database className={clsx(cls, 'text-emerald-500')} aria-hidden="true" />;
        default:
            return <Bell className={clsx(cls, 'text-muted-foreground')} aria-hidden="true" />;
    }
}

function categoryLabel(category: NotificationCategory | string): string {
    switch (category) {
        case 'watchlist_alert': return 'Watchlist';
        case 'feedback_reply': return 'Feedback';
        case 'case_shared': return 'Case';
        case 'import_complete': return 'Import';
        case 'auto_case': return 'Auto-case';
        case 'scraper_run': return 'Data source';
        default: return 'Update';
    }
}

const NotificationRow: React.FC<{
    item: InboxNotification;
    onOpen: (item: InboxNotification) => void;
}> = ({ item, onOpen }) => (
    <button
        type="button"
        role="menuitem"
        onClick={() => onOpen(item)}
        className={clsx(
            'w-full text-left px-3 py-2.5 flex gap-2.5 transition-colors cursor-pointer',
            'hover:bg-accent focus:outline-none focus:bg-accent',
            item.unread && 'bg-primary/5',
        )}
    >
        <span
            className={clsx(
                'mt-1.5 w-1.5 h-1.5 rounded-full shrink-0',
                item.unread ? 'bg-primary' : 'bg-transparent',
            )}
            aria-hidden="true"
        />
        <span className="mt-0.5">{categoryIcon(item.category)}</span>
        <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
                <span className={clsx(
                    'text-sm truncate',
                    item.unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
                )}>
                    {item.title}
                </span>
                <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                    {relativeTime(item.created_at)}
                </span>
            </span>
            {item.body && (
                <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-line break-words">
                    {item.body}
                </span>
            )}
            <span className="sr-only">{categoryLabel(item.category)}</span>
        </span>
    </button>
);

export const NotificationPanel: React.FC = () => {
    const navigate = useNavigate();
    const items = useNotificationStore(s => s.items);
    const loading = useNotificationStore(s => s.loading);
    const error = useNotificationStore(s => s.error);
    const filter = useNotificationStore(s => s.filter);
    const unreadCount = useNotificationStore(s => s.unreadCount);
    const setFilter = useNotificationStore(s => s.setFilter);
    const markRead = useNotificationStore(s => s.markRead);
    const markAllRead = useNotificationStore(s => s.markAllRead);

    const openItem = async (item: InboxNotification) => {
        if (item.unread) await markRead(item.id);
        navigate(hrefForNotification(item));
    };

    return (
        <div
            id="notification-panel"
            role="menu"
            aria-label="Notifications"
            data-testid="notification-panel"
            className="absolute right-0 top-[calc(100%+0.5rem)] w-[22rem] max-w-[calc(100vw-1.5rem)] bg-card border border-border rounded-lg shadow-xl z-[110] overflow-hidden"
        >
            <div className="px-3 py-2.5 border-b border-border flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">Notifications</p>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={() => void markAllRead()}
                        className="text-[11px] text-primary hover:underline flex items-center gap-1 cursor-pointer"
                    >
                        <CheckCheck className="w-3 h-3" aria-hidden="true" />
                        Mark all read
                    </button>
                )}
            </div>
            <div className="px-2 py-1.5 border-b border-border flex gap-1">
                {(['all', 'unread'] as const).map(key => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setFilter(key)}
                        className={clsx(
                            'px-2.5 py-1 rounded-md text-[11px] font-medium capitalize cursor-pointer transition-colors',
                            filter === key
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                        )}
                    >
                        {key}
                    </button>
                ))}
            </div>
            <div className="max-h-80 overflow-y-auto styled-scrollbar">
                {loading && items.length === 0 && (
                    <div className="flex items-center justify-center py-10 text-muted-foreground text-xs gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                        Loading…
                    </div>
                )}
                {error && !loading && (
                    <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                        {error}
                    </div>
                )}
                {!loading && !error && items.length === 0 && (
                    <div className="px-4 py-10 text-center">
                        <Bell className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" aria-hidden="true" />
                        <p className="text-sm text-foreground">You're all caught up</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            Watchlist hits, case shares, and job results land here.
                        </p>
                    </div>
                )}
                {items.map(item => (
                    <NotificationRow key={item.id} item={item} onOpen={openItem} />
                ))}
            </div>
            <div className="border-t border-border px-3 py-2">
                <button
                    type="button"
                    role="menuitem"
                    onClick={() => navigate('/watchlist?tab=events')}
                    className="w-full text-center text-[11px] text-primary hover:underline cursor-pointer"
                >
                    View all alerts
                </button>
            </div>
        </div>
    );
};
